import * as React from 'react'
import { useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { CampaignTrackingCard } from './CampaignTrackingCard'
import { CallTrackingIcon } from './atoms/icons/CallTrackingIcon'
import { MuiTextFieldFormik } from '@sales-center/components'
import { Field, useFormikContext } from 'formik'
import { ProtectedField } from './atoms/components/ProtectedField'
import { ICallTrackingAccount } from '../typings'
import Alert from '@mui/material/Alert'
import { useDisconnectFormikHandler } from '../hooks/useDisconnectFormikHandler'
import { Theme } from '@mui/material/styles'

const styles = {
  iconContainer: {
    lineHeight: '30px',
    '& .close': {
      opacity: 0.3,
    },
  },

  fieldContainer: {
    width: '264px',
    height: '56px',
  },

  button: {
    width: '264px',
  },

  alertContainer: {
    width: '100%',
    backgroundColor: '#fbeaea',
    margin: 'auto',
    color: '#632727',
  },
}

export type Props = {
  id: number
  name: string
  synced: boolean
}

export function CampaignTrackingForm(props: Props) {
  const { id, name, synced } = props
  const formattedName = name.replace(/\s+/g, '')
  const { handleDisconnect } = useDisconnectFormikHandler()

  const {
    submitForm,
    isValid,
    values,
    isSubmitting,
    status,
    setStatus,
    resetForm,
  } = useFormikContext<ICallTrackingAccount>()

  const onDisconnect = useCallback(() => {
    handleDisconnect(id, setStatus)
    resetForm()
  }, [handleDisconnect, id, setStatus, resetForm])

  return (
    <Box display="flex" mb={3} alignItems="center">
      <CampaignTrackingCard>
        <Box display="flex" justifyContent="center">
          <CallTrackingIcon />
        </Box>
        <Box mt={2} style={{ textAlign: 'center' }}>
          <Typography variant="h6">{formattedName}</Typography>
        </Box>
        <Box style={{ textAlign: 'center' }}>
          <Typography variant="body1">
            {status?.synced ?? synced ? 'Synced' : 'Authorize to Sync'}
          </Typography>
        </Box>

        {status?.synced ?? synced ? (
          <>
            <Box
              sx={{
                textAlign: 'center',
                marginTop: (theme: Theme) => theme.spacing(2.6),
              }}
            >
              <Typography variant="body1">Account ID</Typography>
            </Box>

            <Box style={{ textAlign: 'center' }}>
              <Typography variant="h5">{values.accountId}</Typography>
            </Box>

            <Box
              sx={{
                textAlign: 'center',
                marginTop: (theme: Theme) => theme.spacing(3.8),
                marginBottom: (theme: Theme) => theme.spacing(5),
              }}
            >
              <Button
                color="primary"
                variant="outlined"
                sx={styles.button}
                onClick={onDisconnect}
                disabled={!isValid}
              >
                Disconnect / Turn off the Sync
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box style={{ textAlign: 'center' }} mt={3}>
              <Field
                required
                fullWidth
                size="small"
                name="accountId"
                label="Account Id"
                margin="dense"
                sx={styles.fieldContainer}
                component={MuiTextFieldFormik}
                autoComplete="off"
              />
              <ProtectedField
                name="accessKey"
                label="Access Key"
                required={true}
                autoComplete={false}
              />
              <ProtectedField
                name="secretKey"
                label="Secret Key"
                required={true}
                autoComplete={false}
              />
            </Box>

            <Box style={{ textAlign: 'center' }} mt={1} mb={3}>
              <Button
                color="primary"
                variant="contained"
                sx={styles.button}
                onClick={submitForm}
                disabled={!isValid || isSubmitting}
              >
                Sync
              </Button>
            </Box>
          </>
        )}

        {status && status.message && (
          <Box
            m={2}
            sx={{
              width: '295px',
              margin: 'auto',
            }}
          >
            <Alert severity="error" variant="filled" sx={styles.alertContainer}>
              {`Authorize to Sync failure.  We are unable to  ${
                status?.synced ?? synced ? `Disconnect from ` : 'Sync with '
              } ${name}`}
            </Alert>
          </Box>
        )}
      </CampaignTrackingCard>
    </Box>
  )
}
